import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { ExperienceOffer, Filters, getExperienceOffers } from '~/services/ExperiencesService';

import { isVoucherBasedExperienceOffer } from '~/utils/experienceUtils';

import { ExperienceOfferItem } from './useExperienceOffersByPlaceIds';

interface UseExperienceOffersByFiltersParams {
  filters: Filters;
  voucherBasedOnly?: boolean;
  enabled?: boolean;
}

const STALE_TIME = 5 * 60 * 1000;

export function useExperienceOffersByFilters({
  filters,
  voucherBasedOnly = false,
  enabled = true,
}: UseExperienceOffersByFiltersParams) {
  const brand = useSelector((state: App.State) => state.tenant.brand);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['experience-offers', 'by-filters', filters, brand, voucherBasedOnly],
    queryFn: async () => {
      const response = await getExperienceOffers({
        curationData: true,
        limit: 1000,
        filters: {
          ...filters,
          status: ['APPROVED'],
        },
        brand: brand.toLowerCase(),
        sort: 'SCORE',
        sortOrientation: 'DESC',
      });
      const offers = response.result || [];
      return voucherBasedOnly ? offers.filter(isVoucherBasedExperienceOffer) : offers;
    },
    enabled: enabled && !!brand,
    staleTime: STALE_TIME,
  });

  const offers: Array<ExperienceOfferItem> = (data || []).map((offer: ExperienceOffer) => ({
    id: offer.id,
    title: offer.title,
    imageId: offer.images?.[0]?.id,
  }));

  return {
    offers,
    isLoading,
    isError,
  };
}
